import { z } from "zod";
import { zfd } from "zod-form-data";
import { extendZodWithOpenApi } from "zod-openapi";
import { apiLink, formBody, ind, jsonBody, successResponse, tagPaths, unref } from "../util.ts";
import { billing_account_id, datetime_tz, storage_pool_uuid, user_id, uuid } from "./common.ts";
import { Disk, DiskCreateBody } from "./disks.ts";
import * as params from "./params.ts";

extendZodWithOpenApi(z);

export const StoragePoolStatus = z
    .object({
        pool_name: z.string().describe("Backend block storage pool name"),
        stored_gb: z.number().describe("Currently stored data in GB"),
        total_gb: z.number().describe("Total pool capacity in GB"),
        percent_used: z.number().describe("Used capacity as a percentage of total pool capacity"),
    })
    .openapi({ ref: "StoragePoolStatus" });

export const StoragePool = z
    .object({
        uuid: storage_pool_uuid,
        created_at: datetime_tz,
        is_default: z.boolean().describe("Whether new disks are placed into this pool by default"),
        conf: z.string().describe("JSON-encoded pool configuration"),
        percent_used: z.number().describe("Used capacity as a percentage of total pool capacity"),
        ceph_fsid: unref(uuid).describe("Ceph cluster FSID backing this pool"),
        display_name: z.string().describe("Human-readable pool name shown in the UI"),
        stored_gb: z.number().describe("Currently stored data in GB"),
        total_gb: z.number().describe("Total pool capacity in GB"),
        pool_name: z.string().describe("Backend Ceph pool name"),
        stats: z
            .object({
                type: z.string(),
                trash_items_count: z.number(),
                updated_at: datetime_tz,
            })
            .passthrough()
            .nullable()
            .optional(),
    })
    .openapi({ ref: "StoragePool" });

export const StoragePoolResponse = z.array(StoragePool).openapi({ ref: "StoragePoolResponse" });

export const StoragePoolUpdateBody = z
    .object({
        display_name: z.string(),
    })
    .openapi({ ref: "StoragePoolUpdateBody" });

export const StorageIotuneConfig = z
    .object({
        total_bytes_sec: z.number().describe("Steady-state throughput limit in bytes per second").optional(),
        total_bytes_sec_max: z.number().describe("Burst throughput limit in bytes per second").optional(),
        total_bytes_sec_max_length: z.number().describe("Burst throughput duration in seconds").optional(),
        total_iops_sec: z.number().describe("Steady-state IOPS limit").optional(),
        total_iops_sec_max: z.number().describe("Burst IOPS limit").optional(),
        total_iops_sec_max_length: z.number().describe("IOPS burst duration in seconds").optional(),
    })
    .catchall(z.number())
    .openapi({ ref: "StorageIotuneConfig" });

export const AdminDisk = Disk.extend({
    acting_user_id: unref(user_id).describe("User who initiated the latest admin action"),
    image_path: z.string().describe("Backing image path inside the storage pool"),
    storage_pool_name: z.string().describe("Backend storage pool name containing the disk image"),
}).openapi({ ref: "AdminDisk" });

export const AdminDiskCreateBody = DiskCreateBody.extend({
    user_id: zfd.numeric(unref(user_id).describe("Owner of the new disk")),
    billing_account_id: zfd.numeric(billing_account_id),
}).openapi({ ref: "AdminDiskCreateBody" });

export const TrashEmptyReport = z
    .object({ removed_items: z.number(), failed_items: z.number() })
    .openapi({ ref: "TrashEmptyReport" });

export const storageAdminPaths = tagPaths("admin_storage")({
    // Admin storage pools
    "/config/storage/iotune": {
        get: {
            summary: "Get block storage iotune configuration",
            description: "Shows `iotune` parameters used for block storage disk configuration.",
            responses: {
                ...successResponse(StorageIotuneConfig),
            },
        },
        put: {
            summary: "Update block storage iotune configuration",
            description: ind`
            Modifies \`iotune\` parameters for block storage disk configuration.

            The new values affect disks attached, or virtual machines started, after the configuration change.

            When a non-empty JSON object is provided, its keys are written into the Libvirt disk \`iotune\` section. See [Libvirt disk iotune parameters](https://libvirt.org/formatdomain.html#hard-drives-floppy-disks-cdroms) for supported keys and constraints.

            When an empty JSON object \`{}\` is provided, the \`iotune\` section is removed and Libvirt defaults apply.

            When the request body is omitted entirely, the platform applies these predefined defaults instead:
            - \`total_bytes_sec\`: \`209715200\`
            - \`total_iops_sec\`: \`15000\`
            - all other values fall back to Libvirt defaults`,
            requestBody: jsonBody(StorageIotuneConfig),
            responses: {
                ...successResponse(StorageIotuneConfig),
            },
        },
    },
    "/{location}/storage/admin/pool": {
        get: {
            summary: "Get default block storage pool status",
            description: "Returns usage information for the default block storage pool.",
            parameters: [params.location],
            responses: {
                ...successResponse(StoragePoolStatus),
            },
        },
    },
    "/{location}/storage/admin/pools": {
        get: {
            summary: "List storage pools",
            parameters: [params.location],
            responses: {
                ...successResponse(StoragePoolResponse),
            },
        },
    },
    "/{location}/storage/admin/pools/{uuid}": {
        get: {
            summary: "Get storage pool",
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(StoragePool),
            },
        },
        patch: {
            summary: "Update storage pool",
            parameters: [params.location, params.uuid],
            requestBody: formBody(StoragePoolUpdateBody),
            responses: {
                ...successResponse(StoragePool),
            },
        },
        delete: {
            summary: "Delete storage pool",
            description: "Should only be used in debugging and testing scenarios.",
            parameters: [params.location, params.uuid],
            responses: {
                "204": {
                    description: "Storage pool deleted",
                },
            },
        },
    },
    "/{location}/storage/admin/pools/{uuid}/trash/empty": {
        post: {
            summary: "Clean storage pool trash",
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(TrashEmptyReport),
            },
        },
    },
    "/{location}/storage/admin/pools/{uuid}/default": {
        put: {
            summary: "Set storage pool as default",
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(StoragePool),
            },
        },
    },
    "/{location}/storage/admin/disks": {
        post: {
            summary: "Create disk on behalf of a user",
            description: ind`
            Creates a block storage disk on behalf of a user.

            This follows the same source-image rules as [Create disk](${apiLink("/{location}/storage/disks", "block_storage", "post")}).
            Postman currently documents admin-created disks with \`EMPTY\` and \`OS_BASE\` source types.`,
            parameters: [params.location],
            requestBody: formBody(AdminDiskCreateBody),
            responses: {
                ...successResponse(AdminDisk),
            },
        },
    },
    "/{location}/storage/admin/disks/{uuid}/restore": {
        put: {
            summary: "Restore disk from trash",
            description: ind`
            Restores a deleted disk from Ceph trash, if possible.

            The request fails if the disk is not deleted or if the disk has already been purged from trash.`,
            parameters: [params.location, params.uuid],
            responses: {
                ...successResponse(AdminDisk),
            },
        },
    },
});
